import { Question, Response, ResponseOption } from '@bighealth/types';

import { Errors } from 'components/forms';
import { QuizInfo } from 'components/forms/InputField';
import { DropdownItem } from 'components/generic-question/Dropdown';
import * as reporter from 'lib/reporter';
import { stringify } from 'lib/stringify';

export enum QuestionResponseActions {
  ADD_TO_SUBMIT = 'question-response/ADD_TO_SUBMIT',
  CLEAR_TO_SUBMIT = 'question-response/CLEAR_TO_SUBMIT',
  ADD_CORRECT_ANSWERS = 'question-response/ADD_CORRECT_ANSWERS',
  CLEAR_CORRECT_ANSWERS = 'question-response/CLEAR_CORRECT_ANSWERS',
  PUSH = 'question-response/PUSH',
  CLEAR = 'question-response/CLEAR',
  PUSH_QUIZ_ANSWER_BY_SCENE_SET = 'question-response/PUSH_QUIZ_ANSWER_BY_SCENE_SET',
  CLEAR_QUIZ_ANSWER_BY_SCENE_SET = 'question-response/CLEAR_QUIZ_ANSWER_BY_SCENE_SET',
  STORE_FORMIK_STATE_BY_PATH = 'question-response/STORE_FORMIK_STATE_BY_PATH',
  SET_LATEST_QUESTION = 'question-response/SET_LATEST_QUESTION',
  SET_CURRENT_FOCUSED_QUESTION = 'question-response/SET_CURRENT_FOCUSED_QUESTION',
}

// TODO move to @bighealth/types
// TODO change to only number...or...string
export type QuestionId = Question['semantic_id'] | number;

export type FormikPersistableState = {
  values?: Record<QuestionId, DropdownItem[]>;
  errors?: Errors;
  touched?: Record<string, boolean>;
};

export type QuestionResponseAction =
  | {
      type: QuestionResponseActions.ADD_TO_SUBMIT;
      payload?: Record<QuestionId, Response>;
    }
  | { type: QuestionResponseActions.CLEAR_TO_SUBMIT }
  | {
      type: QuestionResponseActions.PUSH;
      payload: Question;
    }
  | { type: QuestionResponseActions.CLEAR }
  | {
      type: QuestionResponseActions.ADD_CORRECT_ANSWERS;
      payload?: Record<string, QuizInfo>;
    }
  | { type: QuestionResponseActions.CLEAR_CORRECT_ANSWERS }
  | {
      type: QuestionResponseActions.PUSH_QUIZ_ANSWER_BY_SCENE_SET;
      payload: {
        path: string;
        attempt: ResponseOption[];
      };
    }
  | {
      type: QuestionResponseActions.CLEAR_QUIZ_ANSWER_BY_SCENE_SET;
      payload: string;
    }
  | {
      type: QuestionResponseActions.STORE_FORMIK_STATE_BY_PATH;
      payload: {
        state: FormikPersistableState;
        path: string;
      };
    }
  | {
      type: QuestionResponseActions.SET_LATEST_QUESTION;
      payload: number | undefined;
    }
  | {
      type: QuestionResponseActions.SET_CURRENT_FOCUSED_QUESTION;
      payload: number | undefined;
    };

export const addQuestionResponseToSubmit = (
  payload: Record<QuestionId, Response>
): QuestionResponseAction => ({
  type: QuestionResponseActions.ADD_TO_SUBMIT,
  payload,
});

// IDEA consistent naming plurality
export const clearQuestionResponsesToSubmit = (): QuestionResponseAction => ({
  type: QuestionResponseActions.CLEAR_TO_SUBMIT,
});

export const addQuestionResponseCorrectAnswers = (
  payload: Record<string, QuizInfo>
): QuestionResponseAction => ({
  type: QuestionResponseActions.ADD_CORRECT_ANSWERS,
  payload,
});

export const clearQuestionResponseCorrectAnswers = (): QuestionResponseAction => ({
  type: QuestionResponseActions.CLEAR_CORRECT_ANSWERS,
});

export const pushQuestionResponse = (
  payload: Question
): QuestionResponseAction => ({
  type: QuestionResponseActions.PUSH,
  payload,
});

export const clearQuestionResponses = (): QuestionResponseAction => ({
  type: QuestionResponseActions.CLEAR,
});

export const pushQuestionResponseQuizAnswersByPath = (
  path: string,
  attempt: ResponseOption[]
): QuestionResponseAction => ({
  type: QuestionResponseActions.PUSH_QUIZ_ANSWER_BY_SCENE_SET,
  payload: { path, attempt },
});

export const clearQuestionResponseQuizAnswersByPath = (
  path: string
): QuestionResponseAction => ({
  type: QuestionResponseActions.CLEAR_QUIZ_ANSWER_BY_SCENE_SET,
  payload: path,
});

export const persistFormikStateByPath = (
  path: string,
  formikState: FormikPersistableState
): {
  type: QuestionResponseActions.STORE_FORMIK_STATE_BY_PATH;
  payload: { path: string; state: FormikPersistableState };
} => {
  if (typeof path !== 'string' /* Defensive coding */) {
    const formikStateStr = stringify(formikState);
    const warnStr = `Expected persistFormikStateByPath path to be string, instead got: ${path}(${typeof path}) for ${formikStateStr}`;
    reporter.log(warnStr, undefined, { silent: true });
  }
  return {
    type: QuestionResponseActions.STORE_FORMIK_STATE_BY_PATH,
    payload: {
      state: formikState,
      path,
    },
  };
};

export const setCurrentFocusedQuestionId = (
  payload: number | undefined
): QuestionResponseAction => ({
  type: QuestionResponseActions.SET_CURRENT_FOCUSED_QUESTION,
  payload,
});

export const setLatestQuestionId = (
  payload: number | undefined
): QuestionResponseAction => ({
  type: QuestionResponseActions.SET_LATEST_QUESTION,
  payload,
});
